import React, { createContext, useEffect, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";

import { AxiosResponse } from "axios";

import Login from "./pages/Login/Login";
import NurseHomepage from "./pages/NurseHomepage/NurseHomepage";
import RodaHomepage from "./pages/RodaHomepage/RodaHomepage";
import { UserDetails, UserDetailsContext } from "./types";
import { routes } from "./api/paths";
import { redirectToHomepage } from "./api/redirect";
import Loading from "./components/Loading";
import GuardedRoute from "./components/GuardedRoute";
import RodaUserAdministration from "./pages/RodaUserAdministration/RodaUserAdministration";
import PasswordSetup from "./pages/Registration/PasswordSetup";
import ExpiredLink from "./pages/Registration/ExpiredLink";
import PasswordReset from "./pages/Registration/PasswordReset";
import PasswordUpdated from "./pages/Registration/PasswordUpdated";
import IdleTimerLogout from "./components/IdleTimerLogout";
import { userLogout } from "./api/logout";
import { getUserDetails } from "./api/user";
import ParseToken from "./pages/Registration/ParseToken";
import RodaRequestsList from "./pages/RodaRequests/RodaRequestsList";
import PublicStatistics from "./pages/PublicStatistics/PublicStatistics";
import AccessDenied from "./pages/AccessDenied/AccessDenied";
import BirthPlanHomepage from "./pages/BirthPlan/BirthPlanHomepage";
import BirthPlanFormPage from "./pages/BirthPlan/BirthPlanFormPage";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const UserContext = createContext<UserDetailsContext>({
  user: null,
  setUser: () => {},
});

export default function Routes() {
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<UserDetails | null>(null);
  const history = useHistory();
  const query = useQuery();

  const onLoginSuccess = (userDetails: UserDetails) => setUser(userDetails);

  const onLogout = async () => {
    await userLogout();
    setUser(null);
    history.push(routes.LOGIN_URL);
  };

  useEffect(() => {
    getUserDetails()
      .then((value: AxiosResponse<UserDetails>) => {
        setUser(value.data);
        setLoading(false);
      })
      .catch((err) => {
        setUser(null);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (user === null) {
    return (
      <Switch>
        <Route path={routes.LOGIN_URL} exact={true}>
          <Login onLoginSuccess={onLoginSuccess} />
        </Route>
        <Route path={routes.PASSWORD_SETUP_URL} exact={true}>
          <PasswordSetup token={query.get("token")} />
        </Route>
        <Route path={routes.EXPIRED_TOKEN_URL} exact={true}>
          <ExpiredLink />
        </Route>
        <Route path={routes.PASSWORD_RESET_URL} exact={true}>
          <PasswordReset />
        </Route>
        <Route path={routes.PASSWORD_UPDATED_URL} exact={true}>
          <PasswordUpdated />
        </Route>
        <Route path={routes.PARSE_TOKEN_URL}>
          <ParseToken token={query.get("token")} />
        </Route>
        <Route path={routes.PUBLIC_STATISTICS_URL} exact={true}>
          <PublicStatistics />
        </Route>
        <Route path={routes.BIRTH_PLAN_URL} exact={true}>
          <BirthPlanHomepage />
        </Route>
        <Route path={routes.BIRTH_PLAN_FORM} exact={true}>
          <BirthPlanFormPage />
        </Route>
        <Route path={routes.ACCESS_DENIED_URL} exact={true}>
          <AccessDenied />
        </Route>
        <Route>
          <Redirect to={routes.LOGIN_URL} />
        </Route>
      </Switch>
    );
  } else {
    return (
      <Switch>
        <Route path={routes.LOGIN_URL}>
          <Redirect to={redirectToHomepage(user.authority)} />
        </Route>
        <Route exact path={"/"}>
          <Redirect to={redirectToHomepage(user.authority)} />
        </Route>
        <UserContext.Provider value={{ user, setUser }}>
          <IdleTimerLogout handleLogout={onLogout} />
          <GuardedRoute
            userRole={user.authority}
            requiredRole="NURSE"
            path={routes.NURSE_HOMEPAGE_URL}
            component={NurseHomepage}
          />
          <GuardedRoute
            userRole={user.authority}
            requiredRole="RODA"
            path={routes.RODA_HOMEPAGE_URL}
            component={RodaHomepage}
          />
          <GuardedRoute
            userRole={user.authority}
            requiredRole="RODA"
            path={routes.RODA_USER_ADMINISTRATION_URL}
            component={RodaUserAdministration}
          />
          <GuardedRoute
            userRole={user.authority}
            requiredRole="RODA"
            path={routes.RODA_REQUESTS_URL}
            component={RodaRequestsList}
          />
          <Route path={routes.PUBLIC_STATISTICS_URL} exact={true}>
            <PublicStatistics />
          </Route>
          <Route path={routes.BIRTH_PLAN_URL} exact={true}>
            <BirthPlanHomepage />
          </Route>
          <Route path={routes.BIRTH_PLAN_FORM} exact={true}>
            <BirthPlanFormPage />
          </Route>
          <Route>
            <Redirect to={redirectToHomepage(user.authority)} />
          </Route>
        </UserContext.Provider>
      </Switch>
    );
  }
}
